import { Component } from '../Component';
import { ArgSpecDictionary, isMobileDevice } from '../ComponentUtils';
import gsap from 'gsap';
import { HHDSDialog, HHDSDialogEvent, HHDSDialogTagName } from '../Dialog/Dialog';
import { HHDSResidenceCard, HHDSResidenceCardAttrNames, HHDSResidenceCardTagName } from '../ResidenceCard/ResidenceCard';
import { PageEvent } from '../../common/page-lifecycle/PageEvent';
import { HHDSButtonResidenceCloseTagName } from '../ButtonResidenceClose/ButtonResidenceClose';
import { CMSFloorplan, FloorPlateData, CMSResidence, fetchResidenceData, parseFloorPlateData } from './FloorPlateData';

const DEBUG_VERBOSE: boolean = false;
export const HHDSFloorPlateTagName: string = 'hhds-floor-plate';
const TAG_NAME: string = HHDSFloorPlateTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;
const FORCE_MOBILE_MODE: boolean = false; // Set to true to force mobile mode for testing purposes
const INFOPANEL_LERP_SPEED: number = 0.15;

export const HHDSFloorPlateAttrNames = {
	theme: 'theme',
	rosAnimation: 'ros-animation',
	enableFloorplans: 'enable-floorplans',
	lang: 'lang',
};

export enum HHDSFloorPlateBuildingTheme {
	ilima = 'ilima',
	melia = 'melia',
}

const Attrs = HHDSFloorPlateAttrNames;

export class HHDSFloorPlate extends Component {
	private floorplateDatas: FloorPlateData[] = [];
	private activeFloorplate: FloorPlateData | null = null;
	private infoPanel!: HTMLElement;
	private infoPanelBacking!: HTMLElement;
	private infoPanelCloseButton!: HTMLElement;
	private paths: SVGPathElement[] = [];
	private listenerFuncs: { [key: string]: any } = {};
	private panelTween: gsap.core.Tween | null = null;
	private mouseMoveSinceFocus: boolean = false;
	private updateIntervalId: number | null = null;
	private infoPanelTargetPos: { x: number; y: number } = { x: 0, y: 0 };
	private infoPanelIsVisible: boolean = false;
	private highlightedPath: SVGPathElement | null = null;
	private useMobileMode: boolean = false;

	constructor() {
		super();

		this.listenerFuncs['mousemove'] = (event: MouseEvent) => {
			this.mouseMoveSinceFocus = true;
			if (!this.dialogOpen && this.infoPanelIsVisible) this.setInfoPanelPosition(event.clientX, event.clientY);
		};
		this.listenerFuncs[HHDSDialogEvent.hideComplete] = () => {
			this.removeDialog();
		};
		this.listenerFuncs[HHDSDialogEvent.hide] = () => {
			this.setInfoPanelVisible(false, true);
		};
	}

	private get desktopOverlayMode(): boolean {
		return !this.useMobileMode;
	}

	private get mobileOverlayMode(): boolean {
		return this.useMobileMode;
	}

	private get dialogOpen(): boolean {
		const dialog: HHDSDialog = this.element<HHDSDialog>(HHDSDialogTagName);
		return !!dialog;
	}

	private constructHtml(): string {
		const plateAreaNote = (position: string, text: string): string => {
			return `<div class="animate ${TAG_NAME}__plate-area-note-${position} ${TAG_NAME}__note-container"><span class="${TAG_NAME}__legend-note uppercase-sm">${text}</span></div>`;
		};

		const legendHtml: string = `
		<div class="${TAG_NAME}__footer-legend-area">
			<div class="${TAG_NAME}__footer-legend">${this.legendItemsHtml}</div>
			<div class="${TAG_NAME}__footer-legend-message"><p class="paragraph-sm">All dimensions are approximate.</p></div>
		</div>`;

		const toolsHtml: string = `
		<div class="${TAG_NAME}__footer-tools">
			<div class="${TAG_NAME}__compass"></div>
			<div class="${TAG_NAME}__elevation"></div>
		</div>`;

		const loadIndicatorHtml: string = `<div class="${TAG_NAME}__load-indicator"><div class="${TAG_NAME}__load-indicator-inner"></div></div>`;

		let clickMessageHtml: string = this.htmlIf(Attrs.enableFloorplans, () => {
			return `<p class="paragraph-sm ${TAG_NAME}__infopanel__click-text">Click to view floorplan</p>`;
		});

		let infoPanelCloseHtml: string = '';
		if (this.mobileOverlayMode) {
			const theme: HHDSFloorPlateBuildingTheme = this.vars.get<HHDSFloorPlateBuildingTheme>(Attrs.theme);
			const themeColor: string = theme == HHDSFloorPlateBuildingTheme.ilima ? 'var(--color-primary-yellow)' : 'var(--color-primary-lychee)';

			let infoPanelCloseStyles: string[] = ['--button-width: 40px;', '--button-height: 33px;'];
			infoPanelCloseStyles.push('--icon-color: var(--color-primary-yellow);');
			infoPanelCloseStyles.push('--icon-color-hover: var(--color-primary-coconut-dark);');
			infoPanelCloseStyles.push(`--icon-color: ${themeColor}`);
			const infoPanelCloseStyle: string = infoPanelCloseStyles.join(' ');

			infoPanelCloseHtml = `<${HHDSButtonResidenceCloseTagName} style="${infoPanelCloseStyle}" class="${TAG_NAME}__infopanel-close"></${HHDSButtonResidenceCloseTagName}>`;
		}

		const html: string = `
			<slot name="floorplate"></slot>
			<slot name="loc"></slot>
      <div class="${TAG_NAME}">
				<div class="${TAG_NAME}__nav-area animate">
					<div class="${TAG_NAME}__nav-row heading heading--03 ${TAG_NAME}--heading-03-sm">
						<div class="${TAG_NAME}__nav-row-heading">
							<span id="level-heading-row">LEVEL:&nbsp;</span>
						</div>
						<div class="${TAG_NAME}__nav-row-items">
							<slot name="nav-row"></slot>
						</div>
					</div>
					<div class="${TAG_NAME}__nav-select-area">
					<span id="level-heading-select" class="heading heading--03 ${TAG_NAME}--heading-03-sm">LEVEL:&nbsp;</span>
						<select class="${TAG_NAME}__nav-select heading heading--03 ${TAG_NAME}--heading-03-sm" name="level-select">
							<slot name="nav-select"></slot>
						</select>
					</div>
					<div class="${TAG_NAME}__nav-message"><p class="paragraph">Choose a level and select a floor plan to see details.</p></div>
				</div>
				<div class="${TAG_NAME}__main">
					<div class="${TAG_NAME}__main-inner">
						<div class="${TAG_NAME}__plate-area">
							<div class="${TAG_NAME}__infopanel-backing"></div>
							<div class="${TAG_NAME}__infopanel ${this.mobileOverlayMode ? `${TAG_NAME}__infopanel--mobile-mode` : ''}">
								${infoPanelCloseHtml}
								<h4 class="heading heading--04">Residence 01</h4>
								<p class="uppercase ${TAG_NAME}__infopanel__plan"></p>
								${loadIndicatorHtml}
								${clickMessageHtml}
							</div>
							<div class="grid">
								<div class="${TAG_NAME}__plate-area-img-container animate col-span-4 md:col-start-2 md:col-span-10 lg:col-start-5 lg:col-span-16"></div>
							</div>
							${plateAreaNote('top', 'Mountain')}
							${plateAreaNote('left', 'Sunset')}
							${plateAreaNote('right', 'Diamond Head')}
							${plateAreaNote('bottom', 'Ocean')}
						</div>
						<div class="${TAG_NAME}__footer animate">
							${legendHtml}
							${toolsHtml}
						</div>
					</div>
				</div>
			</div>
			<div class="${TAG_NAME}__dialog-container"></div>
    `;
		return html;
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		this.useMobileMode = isMobileDevice() || FORCE_MOBILE_MODE || this.currentBreakpoint == 'xs' || this.currentBreakpoint == 'sm';

		this.setInnerHtml('', '', this.constructHtml());

		this.infoPanel = this.element<HTMLElement>(`.${TAG_NAME}__infopanel`);
		this.infoPanelBacking = this.element<HTMLElement>(`.${TAG_NAME}__infopanel-backing`);

		if (this.mobileOverlayMode) this.infoPanel.style.pointerEvents = 'auto';

		gsap.set(this.infoPanel, { opacity: 0 });
		this.setInfoPanelVisible(false);

		this.handleNoteOrientation();
		const floorplateElements = this.elements(`[slot="floorplate"]`);
		parseFloorPlateData(floorplateElements, this.floorplateDatas);
		this.applyLocalisation();
		this.applyTheme();

		document.addEventListener(PageEvent.leave, () => {
			this.removeDialog();
		});

		const navItems = this.elements<HTMLElement>(`[slot="nav-row"]`);
		navItems.forEach((item: HTMLElement) => {
			item.setAttribute('role', 'button');
			item.setAttribute('tabindex', '0');
			item.setAttribute('aria-label', item.innerText);
			item.addEventListener('keydown', (event: KeyboardEvent) => {
				if (event.key === 'Enter') item.click();
			});
			item.addEventListener('click', (_event: MouseEvent) => {
				const span = item.querySelector('span') as HTMLElement;
				if (span?.dataset.floorplateId) {
					DEBUG_VERBOSE && logger.log(LOG_PREFIX, `Activating floor plate: ${span.dataset.floorplateId}`);
					this.activateFloorPlate(span.dataset.floorplateId);
				} else {
					console.warn(`No floorplateId found in nav item: ${item.innerText}`);
				}
			});
		});

		const select = this.element<HTMLSelectElement>(`.${TAG_NAME}__nav-select`);
		select.addEventListener('change', (event: Event) => {
			const target = event.target as HTMLSelectElement;
			if (target.value) this.activateFloorPlate(target.value);
		});

		if (this.floorplateDatas.length > 0) {
			this.activateFloorPlate(this.floorplateDatas[0].floorplateId, true);
		}

		this.infoPanelBacking.addEventListener('click', () => {
			this.setInfoPanelVisible(false);
			if (this.highlightedPath) this.highlightedPath.style.opacity = '0';
		});
		this.infoPanelCloseButton = this.element<HTMLElement>(`.${TAG_NAME}__infopanel-close`);
		this.infoPanelCloseButton?.addEventListener('click', () => {
			this.setInfoPanelVisible(false);
			if (this.highlightedPath) this.highlightedPath.style.opacity = '0';
		});

		this.observeBreakpointChanges((_breakpoint: string) => {
			this.handleNoteOrientation();
		});

		if (this.desktopOverlayMode) this.addEventListener('mousemove', this.listenerFuncs['mousemove']);
	}

	private async fetchResidenceData(floorplate: FloorPlateData): Promise<any> {
		this.setLoadIndicatorVisible(true);
		window.setTimeout(() => {
			const langCode: string = this.vars.get<string>(Attrs.lang);
			fetchResidenceData(langCode, floorplate).then((_success: boolean) => {
				floorplate.pendingResidences = false;
				if (this.highlightedPath) this.populateInfoPanelForSvgPath(this.highlightedPath, this.desktopOverlayMode);
				this.setLoadIndicatorVisible(false);
			});
		}, 1500);
	}

	private setLoadIndicatorVisible(visible: boolean): void {
		const loadIndicator = this.element<HTMLElement>(`.${TAG_NAME}__load-indicator`);
		if (loadIndicator) {
			loadIndicator.style.display = visible ? 'block' : 'none';
		}
	}

	private setInfoPanelEasingActive(active: boolean): void {
		if (!active) {
			if (this.updateIntervalId) window.clearInterval(this.updateIntervalId);
			this.updateIntervalId = null;
			return;
		}
		if (this.updateIntervalId != null) return;
		this.updateIntervalId = window.setInterval(() => {
			if (!this.infoPanelIsVisible) return;
			const currentLeft = parseFloat(this.infoPanel.style.left) || 0;
			const currentTop = parseFloat(this.infoPanel.style.top) || 0;
			const lerpX = gsap.utils.interpolate(currentLeft, this.infoPanelTargetPos.x, INFOPANEL_LERP_SPEED);
			const lerpY = gsap.utils.interpolate(currentTop, this.infoPanelTargetPos.y, INFOPANEL_LERP_SPEED);
			if (Math.abs(currentLeft - lerpX) > 0.1 || Math.abs(currentTop - lerpY) > 0.1) {
				this.infoPanel.style.left = `${lerpX}px`;
				this.infoPanel.style.top = `${lerpY}px`;
			}
		}, 1000 / 60);
	}

	private get legendItemsHtml(): string {
		const dot = (color: string, borderColor: string): string => {
			return `<div style="--dot-color: ${color}; --dot-border-color: ${borderColor};" class="${TAG_NAME}__footer-legend-dot"></div>`;
		};
		const legendItem = (color: string, label: string, id: string): string => {
			return `<div class="${TAG_NAME}__footer-legend-item">${dot(color, '#49372D1A')}<p id="${id}" class="uppercase-sm">${label}</p></div>`;
		};

		const theme = this.vars.get<HHDSFloorPlateBuildingTheme>(Attrs.theme);
		switch (theme) {
			case HHDSFloorPlateBuildingTheme.ilima:
				return `
				${legendItem('#FEF2E3', '2 bed', 'legend-2-bed')}
				${legendItem('#EFEEE5', '3 bed', 'legend-3-bed')}
				${legendItem('#F0E5E2', '4 bed', 'legend-4-bed')}
				${legendItem('#E8E7EA', '5 bed', 'legend-5-bed')}
				${legendItem('#E3ECE7', '6 bed', 'legend-6-bed')}`;
			case HHDSFloorPlateBuildingTheme.melia:
			default:
				return `
				${legendItem('#F2FCFA', '1 bed', 'legend-1-bed')}
				${legendItem('#FFF2E5', '2 bed', 'legend-2-bed')}
				${legendItem('#F0F5E8', '3 bed', 'legend-3-bed')}
				${legendItem('#F2E8EB', '4 bed', 'legend-4-bed')}`;
		}
	}

	private applyLocalisation(): void {
		const locSlot = this.elements<HTMLElement>(`[slot="loc"]`);
		const locElements = Array.from(locSlot);
		const localise = (selector: string, locId: string): void => {
			const element = this.element<HTMLElement>(selector);
			const locElement = locElements.find((el: HTMLElement) => el.id === locId);
			if (element && locElement) element.innerText = locElement.innerText;
		};

		localise(`.${TAG_NAME}__nav-row #level-heading-row`, 'loc-level-heading');
		localise(`.${TAG_NAME}__nav-select-area #level-heading-select`, 'loc-level-heading');
		localise(`.${TAG_NAME}__nav-message p`, 'loc-choose-level');
		for (var i = 1; i <= 6; i++) localise(`.${TAG_NAME}__footer-legend-item #legend-${i}-bed`, `loc-${i}-bed`);
		localise(`.${TAG_NAME}__footer-legend-message p`, 'loc-legend-approx');
		localise(`.${TAG_NAME}__infopanel__click-text`, 'loc-view-floorplan');
	}

	private localisedText(id: string, fallback: string): string {
		const locSlot = this.elements<HTMLElement>(`[slot="loc"]`);
		const locElements = Array.from(locSlot);
		const locElement = locElements.find((el: HTMLElement) => el.id === id);
		if (!locElement) return fallback;
		return locElement ? locElement.innerText : '';
	}

	private applyTheme(): void {
		const component = this.element<HTMLElement>(`.${TAG_NAME}`);

		const theme: HHDSFloorPlateBuildingTheme = this.vars.get<HHDSFloorPlateBuildingTheme>(Attrs.theme);
		switch (theme) {
			case HHDSFloorPlateBuildingTheme.ilima:
				component?.style.setProperty('--infopanel-border-color', 'var(--color-primary-yellow)');
				break;
			case HHDSFloorPlateBuildingTheme.melia:
				component?.style.setProperty('--infopanel-border-color', 'var(--color-primary-lychee)');
				break;
		}
	}

	private populateInfoPanelForSvgPath(path: SVGPathElement, adjustBySizeDifference: boolean = true): boolean {
		if (!path) return false;
		const id: string = path.id;

		this.highlightedPath = path;

		const currentWidth: number = this.infoPanel.offsetWidth;
		const currentHeight: number = this.infoPanel.offsetHeight;

		const h4 = this.infoPanel.querySelector('h4')!;
		const planParagraph = this.infoPanel.querySelector(`.${TAG_NAME}__infopanel__plan`)! as HTMLElement;
		const clickTextParagraph: HTMLElement | null = this.infoPanel.querySelector(`.${TAG_NAME}__infopanel__click-text`);
		const loadIndicator = this.element<HTMLElement>(`.${TAG_NAME}__load-indicator`);

		if (this.activeFloorplate?.pendingResidences) {
			h4.style.display = 'none';
			planParagraph.style.display = 'none';
			if (clickTextParagraph) clickTextParagraph.style.display = 'none';
			loadIndicator.style.display = '';
		} else {
			const residence = this.activeFloorplate?.residences.find((residence: CMSResidence) => residence.svgId === id);
			if (!residence) {
				console.log(`No residence found for SVG id: ${id}`);
				return false;
			}
			h4.innerText = this.localisedText('loc-residence', 'Residence') + ' ' + residence.name;
			h4.style.display = '';
			loadIndicator.style.display = 'none';
			planParagraph.style.display = '';

			let pText: string = '';

			if (residence.planInfo.length) {
				pText += residence.planInfo + '<br />';
			} else {
				if (residence.floorplans.length) {
					const names = residence.floorplans.map((floorplan: CMSFloorplan) => floorplan.name);
					pText += this.localisedText('loc-plan-plural', 'Plans') + ' ' + names.join(', ') + '<br />';
				} else if (residence.floorplans.length == 1) {
					pText += this.localisedText('loc-plan-singular', 'Plan') + ' ' + residence.floorplans[0].name + '<br />';
				} else {
					pText += this.localisedText('loc-no-floorplans', 'No plans available') + '<br />';
				}
			}

			if (residence.roomsSummary?.length) {
				pText += `${residence.roomsSummary}<br />`;
			}
			if (residence.areaRange?.length) {
				pText += `${residence.areaRange}`;
			}

			planParagraph.innerHTML = pText;

			if (clickTextParagraph) {
				if (residence.floorplans.length == 0) {
					clickTextParagraph.style.display = 'none';
				} else {
					clickTextParagraph.style.display = '';
				}
			}
		}

		if (adjustBySizeDifference) {
			this.moveInfoPanelPositionBy(currentWidth - this.infoPanel.offsetWidth, currentHeight - this.infoPanel.offsetHeight);
		}
		return true;
	}

	private setInfoPanelVisible(visible: boolean, force: boolean = false): void {
		this.panelTween?.kill();

		if (!visible && this.highlightedPath) this.highlightedPath.style.opacity = '0';

		const panelBacking = this.element<HTMLElement>(`.${TAG_NAME}__infopanel-backing`);
		if (panelBacking) {
			panelBacking.style.display = visible && this.mobileOverlayMode ? 'block' : 'none';
		}

		if (this.infoPanelIsVisible === visible && !force) {
			return;
		}

		this.infoPanelIsVisible = visible;
		if (visible) {
			this.infoPanel.style.display = 'flex';
			if (this.desktopOverlayMode) {
				this.setInfoPanelEasingActive(true);
				this.infoPanel.style.left = `${this.infoPanelTargetPos.x}px`;
				this.infoPanel.style.top = `${this.infoPanelTargetPos.y}px`;
			} else {
				this.infoPanel.style.left = `50%`;
				this.infoPanel.style.top = `50%`;
				this.infoPanel.style.transform = `translate(-50%, -50%)`;
			}

			gsap.set(this.infoPanel, {
				scale: 0.8,
			});
			this.panelTween = gsap.to(this.infoPanel, {
				opacity: 1,
				scale: 1,
				duration: 0.3,
			});
		} else {
			this.highlightedPath = null;
			this.panelTween = gsap.to(this.infoPanel, {
				opacity: 0,
				duration: 0.3,
				onComplete: () => {
					this.setInfoPanelEasingActive(false);
					this.infoPanel.style.display = 'none';
				},
			});
		}
	}

	private handleNoteOrientation(): void {
		const noteLeft = this.element<HTMLDivElement>(`.${TAG_NAME}__plate-area-note-left`);
		const noteRight = this.element<HTMLDivElement>(`.${TAG_NAME}__plate-area-note-right`);

		const noteLeftWidth = noteLeft.offsetWidth;
		const noteRightWidth = noteRight.offsetWidth;
		const noteLeftHH = noteLeft.offsetHeight / 2;
		const noteRightHH = noteRight.offsetHeight / 2;
		noteLeft.style.transform = `translateX(${-(noteLeftWidth / 2) + noteLeftHH}px) translateY(-50%) rotate(-90deg)`;
		noteRight.style.transform = `translateX(${noteRightWidth / 2 - noteRightHH}px) translateY(-50%) rotate(90deg)`;
	}

	private async activateFloorPlate(id: string, immediate: boolean = false): Promise<void> {
		const imgContainer = this.element<HTMLDivElement>(`.${TAG_NAME}__plate-area-img-container`);
		const data: FloorPlateData | undefined = this.floorplateDatas.find((data: FloorPlateData) => data.floorplateId === id);

		this.setInfoPanelVisible(false, true);

		if (!data) {
			console.warn(`No floorplate data found for id: ${id}`);
			return;
		}

		this.highlightedPath = null;
		this.activeFloorplate = data;

		if (immediate) {
			this.switchSVG(imgContainer, data.svgUrl, immediate);
		} else {
			gsap.to(imgContainer, {
				opacity: 0,
				duration: 0.5,
				onComplete: () => {
					this.switchSVG(imgContainer, data.svgUrl);
				},
			});
		}

		this.fetchResidenceData(this.activeFloorplate);

		const navItems = this.elements<HTMLElement>(`.navitem`);
		navItems.forEach(async (item: HTMLElement) => {
			const span = item.querySelector('span');
			span?.classList.toggle('selected', span?.dataset.floorplateId === id);
			// if (span) {
			// 	// span?.classList.toggle('selected', true);

			// 	requestAnimationFrame(() => {
			// 		const isSelected = span?.dataset.floorplateId === id;
			// 		if (isSelected) {
			// 			console.log('Selected floorplate:', id);
			// 			// span?.classList.toggle('selected', true);
			// 			span.classList.add('selected');
			// 			span.offsetHeight;
			// 		} else {
			// 			console.log('NOT Selected floorplate:', id);
			// 			// span?.classList.toggle('selected', false);
			// 			span.classList.remove('selected');
			// 			span.offsetHeight;
			// 		}
			// 	});
			// }
		});

		const select = this.element<HTMLSelectElement>(`.${TAG_NAME}__nav-select`);
		select.value = id;

		const theme = this.vars.get<HHDSFloorPlateBuildingTheme>(Attrs.theme);

		const compassDiv = this.element<HTMLDivElement>(`.${TAG_NAME}__compass`);
		const compassUrl: string = `/wp-content/themes/mohala-theme/assets/images/compass/compass-${theme}.png`;
		this.load2xImage(compassUrl, (logicalW: string, logicalH: string) => {
			compassDiv.innerHTML = `<img src="${compassUrl}" alt="${id}" style="width: ${logicalW}; height: ${logicalH};">`;
		});

		const elevationDiv = this.element<HTMLDivElement>(`.${TAG_NAME}__elevation`);
		const elevationUrl: string = data.elevation;
		if (data.elevation === '') {
			elevationDiv.innerHTML = '';
		} else {
			elevationDiv.innerHTML = `<img src="${elevationUrl}" alt="${id}"></img>`;
		}
	}

	private async switchSVG(imgContainer: HTMLDivElement, url: string, immediate: boolean = false) {
		const svgElement = await this.loadSvg(url);
		if (!svgElement) {
			return;
		}

		imgContainer.innerHTML = '';
		imgContainer.appendChild(svgElement);

		if (!immediate) {
			gsap.to(imgContainer, {
				opacity: 1,
				duration: 0.5,
			});
		}

		this.parseSvgPaths(svgElement);
	}

	private loadSvg(url: string): Promise<HTMLElement | null> {
		return new Promise((resolve) => {
			fetch(url)
				.then((response) => response.text())
				.then((svgText) => {
					const svgElement = new DOMParser().parseFromString(svgText, 'image/svg+xml').documentElement;
					resolve(svgElement);
				})
				.catch((error) => {
					console.error('Error loading SVG:', error);
					resolve(null);
				});
		});
	}

	private parseSvgPaths(svgElement: HTMLElement): void {
		this.paths = [];
		const floorplateContainer = svgElement.querySelector('g');
		if (!floorplateContainer) return;
		const paths = floorplateContainer.querySelectorAll('path');
		paths.forEach((path) => {
			this.paths.push(path as SVGPathElement);
			this.configurePath(path);
		});
	}

	private configurePath(path: SVGPathElement): void {
		path.style.transition = 'opacity 0.15s ease';
		if (this.vars.get<boolean>(Attrs.enableFloorplans)) {
			path.style.cursor = 'pointer';
		}
		path.style.opacity = '0';

		path.addEventListener('focus', () => {
			if (this.desktopOverlayMode) {
				if (!this.mouseMoveSinceFocus) {
					this.setInfoPanelPosition(20, 20, true, false);
					const didPopulate = this.populateInfoPanelForSvgPath(path, false);
					if (didPopulate) {
						path.style.opacity = '1';
						this.setInfoPanelVisible(true);
					}
				}
				this.mouseMoveSinceFocus = false;
			}
		});
		path.addEventListener('blur', () => {
			if (this.mobileOverlayMode && this.infoPanelIsVisible) {
				return;
			}
			path.style.opacity = '0';
		});

		path.addEventListener('mouseover', (event: MouseEvent) => {
			if (this.desktopOverlayMode) {
				const didPopulate = this.populateInfoPanelForSvgPath(path);
				if (didPopulate) {
					path.style.opacity = '1';
					const panelVisible: boolean = this.infoPanel.style.display != 'flex';
					this.setInfoPanelVisible(true);
					requestAnimationFrame(() => {
						this.setInfoPanelPosition(event.clientX, event.clientY, panelVisible);
					});
				}
			}
		});
		path.addEventListener('mouseout', (_event: MouseEvent) => {
			if (this.desktopOverlayMode) {
				this.setInfoPanelVisible(false);
				path.style.opacity = '0';
			}
		});
		path.addEventListener('click', (event: MouseEvent) => {
			if (this.vars.get<boolean>(Attrs.enableFloorplans)) {
				this.trySelectPath(path);
			} else if (this.mobileOverlayMode) {
				const didPopulate = this.populateInfoPanelForSvgPath(path);
				if (didPopulate) {
					path.style.opacity = '1';
					const panelVisible: boolean = this.infoPanel.style.display != 'flex';
					this.setInfoPanelVisible(true);
					requestAnimationFrame(() => {
						this.setInfoPanelPosition(event.clientX, event.clientY, panelVisible);
					});
				}
			}
		});

		const residence = this.activeFloorplate?.residences.find((residence: CMSResidence) => residence.svgId === path.id);
		if (residence) {
			path.setAttribute('role', 'button');
			path.setAttribute('tabindex', '0');
			path.setAttribute('aria-label', residence.name);
			if (this.vars.get<boolean>(Attrs.enableFloorplans)) {
				path.addEventListener('keydown', (event: KeyboardEvent) => {
					if (event.key === 'Enter') {
						this.trySelectPath(path, true);
						event.preventDefault();
					}
				});
			}
		}
	}

	private get themeColor(): string {
		const theme: HHDSFloorPlateBuildingTheme = this.vars.get<HHDSFloorPlateBuildingTheme>(Attrs.theme);
		const themeColor: string = theme === HHDSFloorPlateBuildingTheme.ilima ? 'var(--color-primary-yellow)' : 'var(--color-primary-lychee)';
		return themeColor;
	}

	private trySelectPath(path: SVGPathElement, autoFocus: boolean = false): void {
		if (this.activeFloorplate?.pendingResidences) return;

		const dialogContainer = this.element(`.${TAG_NAME}__dialog-container`);

		const residence = this.activeFloorplate?.residences.find((residence: CMSResidence) => residence.svgId === path.id);
		if (!residence) {
			console.log(`No residence found for SVG id: ${path.id}`);
			return;
		}

		if (residence.floorplans.length === 0) return;

		const themeColor: string = this.themeColor;
		const html: string = this.getResidenceDialogHtml(residence, residence.floorplans[0], themeColor);
		const dialog = HHDSDialog.create(html, dialogContainer);

		dialog.style.setProperty('--border-color', themeColor);
		dialog.addEventListener(HHDSDialogEvent.hide, this.listenerFuncs[HHDSDialogEvent.hide]);
		dialog.addEventListener(HHDSDialogEvent.hideComplete, this.listenerFuncs[HHDSDialogEvent.hideComplete]);

		const residenceCard: HHDSResidenceCard = dialog.querySelector<HHDSResidenceCard>(HHDSResidenceCardTagName)!;
		residenceCard?.closeButton?.addEventListener('click', () => dialog.hide());

		this.showDialog();

		this.setInfoPanelVisible(false);

		if (autoFocus) {
			requestAnimationFrame(() => {
				const closeButton = dialog.querySelector(HHDSButtonResidenceCloseTagName) as HTMLElement | null;
				closeButton?.focus();
			});
		}
	}

	private getResidenceDialogHtml(residence: CMSResidence, floorplan: CMSFloorplan, themeColor: string): string {
		const floorplanAttr: string = `${HHDSResidenceCardAttrNames.floorplanImgSrc}="${residence.floorplans[0].floorplanImgSrc}"`;
		const floorplanAltAttr: string = `${HHDSResidenceCardAttrNames.floorplanImgAlt}="${residence.floorplans[0].floorplanImgAlt}"`;
		const floorplateAttr: string = `${HHDSResidenceCardAttrNames.floorplateImgSrc}=${residence.floorplateImgSrc}"`;
		const floorplateAltAttr: string = `${HHDSResidenceCardAttrNames.floorplateImgAlt}="${residence.floorplateImgAlt}"`;

		const bodyLine1: string = `<span class="uppercase">${this.localisedText('loc-plan-singular', 'PLAN ')} ${floorplan.name}</span>`;
		const bodyDetails: string[] = [];
		if (floorplan.beds !== undefined) bodyDetails.push(`${floorplan.beds}`);
		if (floorplan.baths !== undefined) bodyDetails.push(`${floorplan.baths}`);
		if (floorplan.interiorArea) bodyDetails.push(`${this.localisedText('loc-interior', 'Interior:')} ${floorplan.interiorArea}`);
		if (floorplan.exteriorArea) bodyDetails.push(`${this.localisedText('loc-exterior', 'Exterior:')} ${floorplan.exteriorArea}`);
		if (floorplan.levels) bodyDetails.push(`${floorplan.levels}`);

		const bodyLeft: string = bodyLine1 + '<br />' + bodyDetails.join('<br />');
		const bodyCenter: string = bodyLine1 + '<br />' + bodyDetails.join(', ');

		let style: string = '';
		style += `--divider-color: ${themeColor}; `;
		style += `--border-color: ${themeColor};`;
		style += `--icon-color: ${themeColor};`;
		style += `--icon-color-hover: var(--color-primary-coconut-dark);`;

		let output: string = `<div class="${TAG_NAME}__dialog-content" slot="content">`;
		output += `<${HHDSResidenceCardTagName} style="${style}" ${floorplanAttr} ${floorplateAttr} ${floorplanAltAttr} ${floorplateAltAttr}>`;
		output += `<span slot="header">${residence.name}</span>`;
		output += `<span slot="eyebrow-text">${this.localisedText('loc-residence', 'RESIDENCE')}</span>`;
		if (floorplan.type && floorplan.type.length) {
			output += `<span slot="plan-type">${floorplan.type}</span>`;
		}
		output += `<span slot="body-left">${bodyLeft}</span>`;
		output += `<span slot="body-center">${bodyCenter}</span>`;
		output += `<span slot="approx-inline">${this.localisedText('loc-approx', 'All dimensions are approximate')}</span>`;
		output += `<span slot="approx-standalone">${this.localisedText('loc-approx', 'All dimensions are approximate')}</span>`;
		output += `</${HHDSResidenceCardTagName}>`;
		output += `</div>`;
		return output;
	}

	private showDialog(): void {
		const dialog: HHDSDialog = this.element<HHDSDialog>(HHDSDialogTagName);
		dialog?.show(true);
	}

	private removeDialog(): void {
		const dialog: HHDSDialog = this.element<HHDSDialog>(HHDSDialogTagName);
		if (dialog) {
			dialog.removeInstantly();
			dialog.remove();
		}
	}

	private moveInfoPanelPositionBy(offsetX: number, offsetY: number): void {
		const currentX: number = parseFloat(this.infoPanel.style.left) || 0;
		const currentY: number = parseFloat(this.infoPanel.style.top) || 0;
		const newX: number = currentX + offsetX;
		const newY: number = currentY + offsetY;
		this.infoPanelTargetPos.x = newX;
		this.infoPanelTargetPos.y = newY;
		this.infoPanel.style.left = `${newX}px`;
		this.infoPanel.style.top = `${newY}px`;
	}

	private setInfoPanelPosition(mouseX: number, mouseY: number, instantUpdate: boolean = false, includeOffsets: boolean = true): void {
		const panelParent = this.infoPanel.parentElement;
		if (!panelParent) return;
		const panelParentRect = panelParent.getBoundingClientRect();

		let targetX: number;
		let targetY: number;
		if (this.mobileOverlayMode) {
			/*console.log(panelParent);
			const plateArea = this.element<HTMLElement>(`.${TAG_NAME}__plate-area`);
			const plateAreaRect = plateArea.getBoundingClientRect();
			console.log(plateAreaRect.top, plateAreaRect.left, plateAreaRect.width);
			targetX = plateAreaRect.width / 2 - this.infoPanel.offsetWidth / 2;
			targetY = plateAreaRect.height / 2 - this.infoPanel.offsetHeight / 2;*/
		} else {
			//  Get the mouse position relative to the panel's parent
			const thisRect = this.getBoundingClientRect();
			const x = mouseX - thisRect.left;
			const y = mouseY - thisRect.top;
			const parentOffsetX = panelParentRect.left - thisRect.left;
			const parentOffsetY = panelParentRect.top - thisRect.top;
			const infoPanelWidth = this.infoPanel.offsetWidth;
			const infoPanelHeight = this.infoPanel.offsetHeight;

			// Calculate the panel's target position, which should match the
			// mouse position and an offset based on the panel's size
			targetX = x - parentOffsetX - infoPanelWidth - (includeOffsets ? 20 : 0);
			targetY = y - parentOffsetY - infoPanelHeight - (includeOffsets ? 10 : 0);

			// Cap the position of the panel to within the bounds of the panel's parent
			const padding: number = 10;
			const minX = padding;
			const maxX = panelParentRect.width - infoPanelWidth - padding;
			const minY = padding;
			const maxY = panelParentRect.height - infoPanelHeight - padding;
			targetX = Math.max(minX, Math.min(maxX, targetX));
			targetY = Math.max(minY, Math.min(maxY, targetY));

			this.infoPanelTargetPos.x = targetX;
			this.infoPanelTargetPos.y = targetY;
			if (instantUpdate) {
				this.infoPanel.style.left = `${targetX}px`;
				this.infoPanel.style.top = `${targetY}px`;
			}
		}
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.panelTween?.kill();
		this.setInfoPanelEasingActive(false);
		if (this.updateIntervalId) {
			window.clearInterval(this.updateIntervalId);
			this.updateIntervalId = null;
		}
		this.removeDialog();
		this.removeEventListener('mousemove', this.listenerFuncs['mousemove']);
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.theme]: {
		description: 'The building theme of the floor plate, which determines coloring.',
		defaultValue: HHDSFloorPlateBuildingTheme.ilima,
		type: HHDSFloorPlateBuildingTheme,
		typeString: 'HHDSFloorPlateBuildingTheme',
	},
	[Attrs.rosAnimation]: {
		description: 'Enable scroll-triggered animations.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.enableFloorplans]: {
		description: 'Enable/disable floorplan viewing functionality.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.lang]: {
		description: 'The language code for localisation.',
		defaultValue: '',
		type: String,
	},
};
